import React, { useState, useEffect, useCallback, FC } from "react";
import baseApi from "utils/axios-instance";
import { Mixpanel } from "utils/Analytics";
import {
  CreditCard,
  Loader2,
  Receipt,
  RefreshCw,
  Shield,
} from "lucide-react";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import { Badge } from "components/ui/badge";
import { Input } from "components/ui/input";
import { Alert, AlertDescription } from "components/ui/alert";
import { Toaster } from "components/ui/sonner";
import { toast } from "sonner";
import {
  STRIPE_BILLING_URL,
  STRIPE_BILLING_TEST_URL,
} from "utils/constants";
import { isLocalhost, isStaging, isDemo } from "utils/axios-instance";

interface BillingSubscription {
  price_per_inquiry_usd: number;
  monthly_minimum_usd: number;
}

interface BillingInvoice {
  id: string;
  amount_usd: number;
  created_ts: string | null;
  summary: string;
  status: string;
}

interface BillingData {
  team_name: string;
  stripe_payment_method: string | null;
  has_payment_method: boolean;
  payment_details_configured: boolean;
  invoice_email: string | null;
  subscription: BillingSubscription | null;
  invoices: BillingInvoice[];
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  AUTOCHARGE: "Auto-pay (credit card)",
  INVOICE: "Invoice (manual payment)",
};

const INVOICE_STATUS_LABELS: Record<string, string> = {
  paid: "Paid",
  open: "Unpaid",
  not_sent: "Not sent",
  sent: "Sent",
  draft: "Draft",
  void: "Void",
  uncollectible: "Uncollectible",
  unknown: "Unknown",
};

function getInvoiceStatusLabel(status: string): string {
  if (!status || status === "unknown") return "Open";
  return INVOICE_STATUS_LABELS[status] || status;
}

function getInvoiceStatusVariant(
  status: string
): "default" | "secondary" | "destructive" | "outline" {
  const normalizedStatus = !status || status === "unknown" ? "open" : status;
  if (normalizedStatus === "paid") return "default";
  if (normalizedStatus === "open" || normalizedStatus === "uncollectible")
    return "destructive";
  if (
    normalizedStatus === "not_sent" ||
    normalizedStatus === "draft" ||
    normalizedStatus === "void"
  )
    return "secondary";
  return "outline";
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

function formatDate(isoDate: string | null): string {
  if (!isoDate) return "—";
  return new Date(isoDate).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const BillingPage: FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [billingData, setBillingData] = useState<BillingData | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    string | null
  >(null);
  const [invoiceEmail, setInvoiceEmail] = useState("");

  const stripeBillingUrl =
    isLocalhost || isStaging || isDemo
      ? STRIPE_BILLING_TEST_URL
      : STRIPE_BILLING_URL;

  const fetchBilling = useCallback(async () => {
    setLoading(true);
    try {
      const response = await baseApi.get("/dashboard/billing/data", {
        withCredentials: true,
      });
      setBillingData(response.data);
      setSelectedPaymentMethod(response.data.stripe_payment_method);
      setInvoiceEmail(response.data.invoice_email || "");
    } catch (error: any) {
      Mixpanel.track("Error: Billing page load", { error: error.message });
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to load billing information.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  const handleSaveBillingSettings = async () => {
    if (!selectedPaymentMethod) {
      toast.error("Please select a billing preference.");
      return;
    }

    if (!invoiceEmail.trim()) {
      toast.error("Please enter an invoice email.");
      return;
    }

    setSaving(true);
    try {
      await baseApi.put(
        "/dashboard/billing/data",
        {
          stripe_payment_method: selectedPaymentMethod,
          invoice_email: invoiceEmail.trim(),
        },
        { withCredentials: true }
      );
      Mixpanel.track("Billing settings updated", {
        stripe_payment_method: selectedPaymentMethod,
      });
      toast.success("Billing settings updated.");
      await fetchBilling();
    } catch (error: any) {
      Mixpanel.track("Error: Billing settings update", {
        error: error.message,
      });
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to update billing settings.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSyncPaymentMethod = async () => {
    setSyncing(true);
    try {
      const response = await baseApi.post(
        "/dashboard/billing/sync_payment_method",
        {},
        { withCredentials: true }
      );
      if (response.data?.has_payment_method) {
        toast.success("Payment method synced successfully.");
        await fetchBilling();
      } else {
        toast.error(
          "No payment method found. Please add a credit card in Stripe first."
        );
      }
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to sync payment method.";
      toast.error(message);
    } finally {
      setSyncing(false);
    }
  };

  const hasUnsavedBillingChanges =
    billingData &&
    (selectedPaymentMethod !== billingData.stripe_payment_method ||
      invoiceEmail.trim() !== (billingData.invoice_email || ""));

  return (
    <div className="w-full">
      <Toaster richColors position="top-center" />
      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-6">
            <div className="p-3 bg-[#005151] rounded-xl">
              <CreditCard className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Billing</h1>
              <p className="text-gray-600 mt-1">
                {billingData?.team_name
                  ? `Manage billing for ${billingData.team_name}`
                  : "Manage your team's billing and invoices"}
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <Card className="border border-gray-200 shadow-sm">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="p-4 bg-[#005151]/10 rounded-full mb-4">
                <Loader2 className="h-8 w-8 animate-spin text-[#005151]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Loading billing information
              </h3>
              <p className="text-gray-600">
                Please wait while we fetch your team's billing details...
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            <Card className="border border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-[#005151]/10 rounded-lg">
                    <CreditCard className="h-5 w-5 text-[#005151]" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-semibold text-gray-900">
                      Payment Settings
                    </CardTitle>
                    <CardDescription className="text-gray-600 mt-1">
                      Configure how your team is billed each month
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <Alert>
                  <Shield className="h-4 w-4" />
                  <AlertDescription>
                    Payment information is secured with Stripe. You can update
                    your card at any time.
                  </AlertDescription>
                </Alert>

                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-900">
                      Billing preference
                    </label>
                    <Select
                      value={selectedPaymentMethod || undefined}
                      onValueChange={setSelectedPaymentMethod}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select billing preference" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AUTOCHARGE">
                          {PAYMENT_METHOD_LABELS.AUTOCHARGE}
                        </SelectItem>
                        <SelectItem value="INVOICE">
                          {PAYMENT_METHOD_LABELS.INVOICE}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      Auto-pay charges your card automatically. Invoice billing
                      sends a monthly invoice for manual payment.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-900">
                      Invoice email
                    </label>
                    <Input
                      type="email"
                      value={invoiceEmail}
                      onChange={(e) => setInvoiceEmail(e.target.value)}
                      placeholder="billing@example.com"
                    />
                    <p className="text-xs text-gray-500">
                      Invoices and billing notifications are sent to this
                      address.
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-900">
                    Payment method on file
                  </label>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={
                        billingData?.has_payment_method
                          ? "default"
                          : "secondary"
                      }
                    >
                      {billingData?.has_payment_method
                        ? "Card on file"
                        : "No card on file"}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        window.open(
                          stripeBillingUrl,
                          "_blank",
                          "noopener,noreferrer"
                        )
                      }
                      className="border-[#005151] text-[#005151] hover:bg-[#005151] hover:text-white"
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      Manage in Stripe
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSyncPaymentMethod}
                      disabled={syncing}
                    >
                      {syncing ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      Sync payment method
                    </Button>
                  </div>
                </div>

                {hasUnsavedBillingChanges && (
                  <div className="flex justify-end">
                    <Button
                      onClick={handleSaveBillingSettings}
                      disabled={saving}
                      className="bg-[#005151] hover:bg-[#004040] text-white"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save billing settings"
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {billingData?.subscription && (
              <Card className="border border-gray-200 shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-[#005151]/10 rounded-lg">
                      <Receipt className="h-5 w-5 text-[#005151]" />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-semibold text-gray-900">
                        Plan Details
                      </CardTitle>
                      <CardDescription className="text-gray-600 mt-1">
                        Your team's current pricing
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <dl className="grid gap-4 sm:grid-cols-2">
                    {billingData.subscription.price_per_inquiry_usd > 0 && (
                      <div>
                        <dt className="text-sm text-gray-500">Price per call</dt>
                        <dd className="text-lg font-semibold text-gray-900">
                          {formatCurrency(
                            billingData.subscription.price_per_inquiry_usd
                          )}
                        </dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-sm text-gray-500">
                        Monthly minimum
                      </dt>
                      <dd className="text-lg font-semibold text-gray-900">
                        {formatCurrency(
                          billingData.subscription.monthly_minimum_usd
                        )}
                      </dd>
                    </div>
                  </dl>
                </CardContent>
              </Card>
            )}

            <Card className="border border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-[#005151]/10 rounded-lg">
                    <Receipt className="h-5 w-5 text-[#005151]" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-semibold text-gray-900">
                      Invoice History
                    </CardTitle>
                    <CardDescription className="text-gray-600 mt-1">
                      Past invoices for your team
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {billingData?.invoices && billingData.invoices.length > 0 ? (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="hidden sm:table-cell">
                            Summary
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {billingData.invoices.map((invoice) => (
                          <TableRow key={invoice.id}>
                            <TableCell className="whitespace-nowrap">
                              {formatDate(invoice.created_ts)}
                            </TableCell>
                            <TableCell className="font-medium">
                              {formatCurrency(invoice.amount_usd)}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={getInvoiceStatusVariant(
                                  invoice.status
                                )}
                              >
                                {getInvoiceStatusLabel(invoice.status)}
                              </Badge>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell text-gray-600 max-w-xs truncate">
                              {invoice.summary || "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-gray-600 text-sm py-4 text-center">
                    No invoices yet. Invoices will appear here after your first
                    billing cycle.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingPage;
